"use client";

import { Lock } from "lucide-react";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface LoginPromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
}

/**
 * Small modal shown to unauthenticated users when they try to perform an
 * action that requires an account (e.g. reporting content). Offers Login
 * and Create Account links.
 */
export function LoginPromptDialog({
  open,
  onOpenChange,
  title = "Login required",
  description = "Please log in or create an account to continue.",
}: LoginPromptDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[420px]"
        // React events bubble through the component tree even when
        // rendered in a portal. If this dialog is mounted inside a
        // clickable Card (e.g. a forum card), the Login / Create
        // account Link clicks would otherwise reach the Card's onClick
        // and `router.push` would race the Link, sending the user back
        // to the card's detail page instead of /login.
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
            <Lock className="h-6 w-6 text-blue-600" />
          </div>
          <DialogTitle className="text-center">{title}</DialogTitle>
          <DialogDescription className="text-center">
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button asChild className="w-full bg-blue-600 hover:bg-blue-700">
            <Link href="/login" onClick={(e) => e.stopPropagation()}>
              Login
            </Link>
          </Button>
          <Button asChild variant="outline" className="w-full">
            <Link href="/register" onClick={(e) => e.stopPropagation()}>
              Create account
            </Link>
          </Button>
          <Button
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
